import { getLineHeight } from "@excalidraw/common";

import type { ExcalidrawElementSkeleton } from "@excalidraw/element";

export type MockAITemplateType =
  | "presentation"
  | "sales-funnel"
  | "process"
  | "mind-map"
  | "customer-journey"
  | "crm";

type GenerateTemplateOptions = {
  prompt: string;
  type: MockAITemplateType;
};

type Point = {
  x: number;
  y: number;
};

type CardConfig = {
  id: string;
  label: string;
  position: Point;
  fill: string;
  stroke: string;
};

const FONT_FAMILY = 1;
const LINE_HEIGHT = getLineHeight(FONT_FAMILY);

const palette = {
  ink: "#172033",
  muted: "#526173",
  arrow: "#64748b",
  blueFill: "#e0f2fe",
  blueStroke: "#38bdf8",
  greenFill: "#dcfce7",
  greenStroke: "#22c55e",
  purpleFill: "#ede9fe",
  purpleStroke: "#8b5cf6",
  amberFill: "#fef3c7",
  amberStroke: "#f59e0b",
  metricsFill: "#f8fafc",
  metricsStroke: "#bfdbfe",
  followUpFill: "#ecfdf5",
  followUpStroke: "#86efac",
};

const normalizePromptTitle = (prompt: string) => {
  const cleaned = prompt
    .replace(/^(crie|criar|gere|gerar|monte|montar)\s+/i, "")
    .replace(/^um[a]?\s+/i, "")
    .trim();

  if (!cleaned) {
    return "Apresentação visual";
  }

  return cleaned
    .split(/\s+/)
    .map((word) =>
      word.length <= 3
        ? word.toLowerCase()
        : `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`,
    )
    .join(" ");
};

const createText = (
  text: string,
  x: number,
  y: number,
  options: Record<string, unknown> = {},
): ExcalidrawElementSkeleton =>
  ({
    type: "text",
    x,
    y,
    text,
    strokeColor: palette.ink,
    fontSize: 28,
    fontFamily: FONT_FAMILY,
    lineHeight: LINE_HEIGHT,
    ...options,
  }) as ExcalidrawElementSkeleton;

const createContainer = ({
  x,
  y,
  width,
  height,
  fill,
  stroke,
  strokeWidth = 2,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  fill: string;
  stroke: string;
  strokeWidth?: number;
}): ExcalidrawElementSkeleton => ({
  type: "rectangle",
  x,
  y,
  width,
  height,
  strokeColor: stroke,
  backgroundColor: fill,
  fillStyle: "solid",
  strokeWidth,
  roughness: 0.7,
  roundness: { type: 3 },
});

const createCard = ({
  id,
  label,
  position,
  fill,
  stroke,
}: CardConfig): ExcalidrawElementSkeleton => ({
  id,
  type: "rectangle",
  x: position.x,
  y: position.y,
  width: 250,
  height: 110,
  strokeColor: stroke,
  backgroundColor: fill,
  fillStyle: "solid",
  strokeWidth: 2,
  roughness: 0.75,
  roundness: { type: 3 },
  label: {
    text: label,
    fontSize: 22,
    fontFamily: FONT_FAMILY,
    strokeColor: palette.ink,
  },
});

const createArrow = ({
  id,
  fromId,
  toId,
  start,
  end,
}: {
  id: string;
  fromId: string;
  toId: string;
  start: Point;
  end: Point;
}): ExcalidrawElementSkeleton => ({
  id,
  type: "arrow",
  x: start.x,
  y: start.y,
  width: end.x - start.x,
  height: end.y - start.y,
  strokeColor: palette.arrow,
  strokeWidth: 2,
  roughness: 0.8,
  roundness: { type: 2 },
  startArrowhead: null,
  endArrowhead: "arrow",
  start: { id: fromId },
  end: { id: toId },
});

const createTitledBlock = ({
  title,
  lines,
  x,
  y,
  width,
  height,
  fill,
  stroke,
  titleSize = 26,
  bodySize = 20,
}: {
  title: string;
  lines: readonly string[];
  x: number;
  y: number;
  width: number;
  height: number;
  fill: string;
  stroke: string;
  titleSize?: number;
  bodySize?: number;
}): ExcalidrawElementSkeleton[] => [
  createContainer({ x, y, width, height, fill, stroke }),
  createText(title, x + 30, y + 28, {
    fontSize: titleSize,
    strokeColor: palette.ink,
  }),
  createText(lines.join("\n"), x + 32, y + 82, {
    fontSize: bodySize,
    lineHeight: LINE_HEIGHT,
    strokeColor: palette.muted,
  }),
];

const createMetricsBlock = (): ExcalidrawElementSkeleton[] =>
  createTitledBlock({
    title: "Métricas principais",
    lines: [
      "Leads gerados",
      "Taxa de resposta",
      "Agendamentos",
      "Conversão",
      "Receita prevista",
    ],
    x: 1440,
    y: 190,
    width: 360,
    height: 360,
    fill: palette.metricsFill,
    stroke: palette.metricsStroke,
    titleSize: 26,
    bodySize: 21,
  });

const createFollowUpBlock = (): ExcalidrawElementSkeleton[] =>
  createTitledBlock({
    title: "Plano de Follow-up",
    lines: [
      "1. Enviar mensagem pós-atendimento",
      "2. Confirmar agendamentos",
      "3. Recuperar leads sem resposta",
      "4. Pedir avaliação",
      "5. Campanha de indicação",
    ],
    x: 120,
    y: 590,
    width: 1320,
    height: 250,
    fill: palette.followUpFill,
    stroke: palette.followUpStroke,
    titleSize: 28,
    bodySize: 21,
  });

const createSalesFunnelCards = (): CardConfig[] => {
  const rowOneY = 210;
  const rowTwoY = 390;
  const rowOneX = [120, 442, 764, 1086];
  const rowTwoX = [281, 603, 925];
  const colors = [
    [palette.blueFill, palette.blueStroke],
    [palette.greenFill, palette.greenStroke],
    [palette.purpleFill, palette.purpleStroke],
    [palette.amberFill, palette.amberStroke],
    [palette.blueFill, palette.blueStroke],
    [palette.greenFill, palette.greenStroke],
    [palette.purpleFill, palette.purpleStroke],
  ] as const;
  const labels = [
    "Captação de leads",
    "Atendimento inicial",
    "Qualificação",
    "Agendamento",
    "Consulta/procedimento",
    "Follow-up",
    "Recompra/indicação",
  ];

  return labels.map((label, index) => {
    const isFirstRow = index < 4;
    const position = {
      x: isFirstRow ? rowOneX[index] : rowTwoX[index - 4],
      y: isFirstRow ? rowOneY : rowTwoY,
    };
    const [fill, stroke] = colors[index];

    return {
      id: `ai-funnel-card-${index + 1}`,
      label,
      position,
      fill,
      stroke,
    };
  });
};

const createSalesFunnelArrows = (
  cards: readonly CardConfig[],
): ExcalidrawElementSkeleton[] => {
  const cardWidth = 250;
  const cardHeight = 110;
  const centerY = (card: CardConfig) => card.position.y + cardHeight / 2;

  return [
    createArrow({
      id: "ai-funnel-arrow-1",
      fromId: cards[0].id,
      toId: cards[1].id,
      start: { x: cards[0].position.x + cardWidth + 18, y: centerY(cards[0]) },
      end: { x: cards[1].position.x - 18, y: centerY(cards[1]) },
    }),
    createArrow({
      id: "ai-funnel-arrow-2",
      fromId: cards[1].id,
      toId: cards[2].id,
      start: { x: cards[1].position.x + cardWidth + 18, y: centerY(cards[1]) },
      end: { x: cards[2].position.x - 18, y: centerY(cards[2]) },
    }),
    createArrow({
      id: "ai-funnel-arrow-3",
      fromId: cards[2].id,
      toId: cards[3].id,
      start: { x: cards[2].position.x + cardWidth + 18, y: centerY(cards[2]) },
      end: { x: cards[3].position.x - 18, y: centerY(cards[3]) },
    }),
    createArrow({
      id: "ai-funnel-arrow-4",
      fromId: cards[3].id,
      toId: cards[4].id,
      start: {
        x: cards[3].position.x + cardWidth / 2,
        y: cards[3].position.y + cardHeight + 18,
      },
      end: {
        x: cards[4].position.x + cardWidth / 2,
        y: cards[4].position.y - 18,
      },
    }),
    createArrow({
      id: "ai-funnel-arrow-5",
      fromId: cards[4].id,
      toId: cards[5].id,
      start: { x: cards[4].position.x + cardWidth + 18, y: centerY(cards[4]) },
      end: { x: cards[5].position.x - 18, y: centerY(cards[5]) },
    }),
    createArrow({
      id: "ai-funnel-arrow-6",
      fromId: cards[5].id,
      toId: cards[6].id,
      start: { x: cards[5].position.x + cardWidth + 18, y: centerY(cards[5]) },
      end: { x: cards[6].position.x - 18, y: centerY(cards[6]) },
    }),
  ];
};

const createSalesFunnelTemplate = (
  prompt: string,
): ExcalidrawElementSkeleton[] => {
  const cards = createSalesFunnelCards();

  return [
    createText("Funil de Vendas para Clínica Estética", 120, 70, {
      fontSize: 44,
      strokeColor: palette.ink,
    }),
    createText(normalizePromptTitle(prompt), 124, 132, {
      fontSize: 20,
      strokeColor: palette.muted,
    }),
    ...cards.map(createCard),
    ...createSalesFunnelArrows(cards),
    ...createMetricsBlock(),
    ...createFollowUpBlock(),
  ];
};

const genericCardsByType: Record<MockAITemplateType, readonly string[]> = {
  presentation: ["Contexto", "Problema", "Plano", "Próximos passos"],
  "sales-funnel": [],
  process: ["Entrada", "Execução", "Revisão", "Entrega"],
  "mind-map": ["Ideias", "Público", "Canais", "Ações"],
  "customer-journey": ["Descoberta", "Consideração", "Compra", "Retenção"],
  crm: ["Leads", "Pipeline", "Atividades", "Resultados"],
};

const createGenericTemplate = ({
  prompt,
  type,
}: GenerateTemplateOptions): ExcalidrawElementSkeleton[] => {
  const labels = genericCardsByType[type];
  const ids = labels.map((_, index) => `ai-generic-card-${index + 1}`);
  const title = normalizePromptTitle(prompt);
  const elements: ExcalidrawElementSkeleton[] = [
    createText(title, 120, 80, { fontSize: 38 }),
    createText("Board visual editável gerado localmente", 124, 136, {
      fontSize: 20,
      strokeColor: palette.muted,
    }),
  ];

  labels.forEach((label, index) => {
    const x = 120 + index * 320;

    elements.push(
      createCard({
        id: ids[index],
        label,
        position: { x, y: 230 },
        fill: index % 2 === 0 ? palette.blueFill : palette.greenFill,
        stroke: index % 2 === 0 ? palette.blueStroke : palette.greenStroke,
      }),
    );

    if (index > 0) {
      elements.push(
        createArrow({
          id: `ai-generic-arrow-${index}`,
          fromId: ids[index - 1],
          toId: ids[index],
          start: { x: x - 52, y: 285 },
          end: { x: x - 18, y: 285 },
        }),
      );
    }
  });

  elements.push(
    ...createTitledBlock({
      title: "Notas",
      lines: [
        "Ajuste textos e blocos livremente",
        "Use Salvar board quando quiser persistir",
      ],
      x: 120,
      y: 430,
      width: 860,
      height: 160,
      fill: palette.metricsFill,
      stroke: palette.metricsStroke,
    }),
  );

  return elements;
};

export const generateExcalidrawTemplate = (
  options: GenerateTemplateOptions,
): ExcalidrawElementSkeleton[] => {
  if (
    options.type === "sales-funnel" ||
    /funil/i.test(options.prompt) ||
    /clinica estetica|clínica estética/i.test(options.prompt)
  ) {
    return createSalesFunnelTemplate(options.prompt);
  }

  return createGenericTemplate(options);
};
